// Claude im Homelab (docs/claude.md): der STAND aller Chats -- Titel,
// Repo, Zustand, offene Rückfragen (nur Werkzeug und Zeit), Verbrauch.
// Ohne Inhalte und darum ohne PIN; Baum, Aktivitätsleiste, „läuft gerade"
// und die Übersicht lesen ihn. GET /v2-api/claude ist Boot-Hydration und
// Netz (Revalidator bei Fokuswechsel und Wiederverbinden); jede Änderung
// kommt seit 03.11.2026 in Teilen über das Homelab-Protokoll (Topics
// claude.lage, claude.chat, claude.commits, claude.repo; lib/claude/protokoll.ts,
// docs/protokoll.md › „Claude über das Protokoll“). Vorher schickte der
// WS-Kanal "claude" bei jeder Änderung den ganzen Stand (~100 kB).
//
// Die INHALTE eines Chats hält lib/claude/chat.svelte.ts (eigener Socket
// mit PIN) -- seit der Sicherheits-Nachprüfung 21.10.2026 (Befund 2) auch
// die offenen Rückfragen ganz (Befehl, Datei, Fragen an dich).

import { SvelteMap } from "svelte/reactivity";

import { putJSON } from "./api";

import type { Chat, ClaudeAntwort } from "./claude/claude";
import { entferne, instanzOrt, leereTeile, liesJson, mischeRest, ortSchluessel, setze, teileAus, standAus, type Teile } from "./claude/protokoll";
import { loadPersisted, persist } from "./persistent-summary.svelte";
import { abonniere } from "./protokoll/client.svelte";
import { registerRevalidator } from "./revalidate.svelte";

const STORAGE_KEY = "claude";

const _a = new SvelteMap<string, ClaudeAntwort>();
let _started = false;
let _abos: Array<() => void> = [];

// Die Teile, wie sie zuletzt kamen; dazu je Teil der JSON-Text (ein
// Lebenszeichen oder Snapshot mit demselben Text baut nichts neu) und wann
// das Protokoll ihn zuletzt geändert hat (Zähler `_zug`, für das Mischen mit
// REST) -- wie beim Argus-Stand.
let _teile: Teile = leereTeile();
const _roh = new Map<string, string>();
const _geaendert = new Map<string, number>();
let _zug = 0;
let _baut = false;

function uebernimm(a: ClaudeAntwort): void {
  if (!a || !a.stand || !Array.isArray(a.stand.chats)) return;
  _a.set("current", a);
  persist(STORAGE_KEY, a);
}

// Baut die Antwort einmal je Runde: ein Abo bringt ~80 Instanzen auf einmal.
function baueBald(): void {
  if (_baut) return;
  _baut = true;
  queueMicrotask(() => {
    _baut = false;
    const a = standAus(_teile);
    if (a) uebernimm(a);
  });
}

function beiStand(inst: string, daten: unknown): void {
  const ort = instanzOrt(inst);
  const j = ort && liesJson(daten);
  if (!ort || !j) return;
  const k = ortSchluessel(ort);
  if (_roh.get(k) === j.roh) return;
  _roh.set(k, j.roh);
  _geaendert.set(k, ++_zug);
  setze(_teile, ort, j.wert);
  baueBald();
}

function beiWeg(inst: string): void {
  const ort = instanzOrt(inst);
  if (!ort) return;
  const k = ortSchluessel(ort);
  _roh.delete(k);
  _geaendert.set(k, ++_zug);
  entferne(_teile, ort);
  baueBald();
}

async function refresh(): Promise<void> {
  const ab = _zug;
  try {
    const r = await fetch("/v2-api/claude", { credentials: "include" });
    if (!r.ok) return;
    const rest = (await r.json()) as ClaudeAntwort;
    if (!rest?.stand || !Array.isArray(rest.stand.chats)) return;
    mischeRest(_teile, rest, (k) => (_geaendert.get(k) ?? -1) > ab);
    // Die Texte gelten nicht mehr für das Gemischte: der nächste Delta baut neu.
    _roh.clear();
    baueBald();
  } catch {
    // Dienst startet neu -- das Protokoll oder der Revalidator holt es nach.
  }
}

export function startClaude(): void {
  if (_started) return;
  _started = true;
  registerRevalidator(refresh);
  const cached = loadPersisted<ClaudeAntwort>(STORAGE_KEY);
  if (cached?.stand && Array.isArray(cached.stand.chats)) {
    _a.set("current", cached);
    _teile = teileAus(cached);
  }
  void refresh();
  const h = { stand: beiStand, weg: beiWeg };
  _abos = [
    abonniere("claude.lage", { rate: "normal" }, h),
    abonniere("claude.chat.*.stand", { rate: "normal" }, h),
    abonniere("claude.chat.*.commits", { rate: "normal" }, h),
    abonniere("claude.repo.*.stand", { rate: "normal" }, h),
  ];
}

export function stopClaude(): void {
  _started = false;
  for (const ab of _abos) ab();
  _abos = [];
  _teile = leereTeile();
  _roh.clear();
  _geaendert.clear();
  _a.clear();
}

export function getClaude(): ClaudeAntwort | undefined {
  return _a.get("current");
}

export function claudeChat(id: string): Chat | undefined {
  return _a.get("current")?.stand.chats.find((c) => c.id === id);
}

/** Claude fragt vor Befehlen (false) oder erlaubt selbst (true) --
 *  internal/claude/freigaben.go, Tims Wahl 21.10.2026. Braucht die PIN. */
export async function setzeOhneRueckfrage(an: boolean): Promise<void> {
  await putJSON("/v2-api/claude/freigaben", { ohne_rueckfrage: an });
}
